import { ArrowLeft } from '@medusajs/icons';
import { Button } from '@pcrm/fe/ui-kit';
import { Link } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * De unde s-a deschis pagina unui document — ca „Înapoi" să ducă acolo
 * ([#222](https://github.com/bplugaru/efx-pcrm/issues/222)).
 *
 * Fără el, butonul ducea mereu la registru: operatorul care lucra în fișa unui client, pe
 * tabul Documente, ajungea după o corectură în lista tuturor actelor și își căuta clientul
 * de la capăt.
 *
 * ⚠️ **Originea stă în adresă, nu se deduce.** Nici din `document.customerId` — registrul
 * deschide și el acte care au client, iar de acolo „Înapoi" trebuie să ducă tot la registru —,
 * nici din `history.back()`, care nu duce nicăieri pe o pagină deschisă direct din link.
 *
 * O valoare stricată cade pe registru, nu dă eroare: `?client=abc` deschide documentul.
 */
export const documentSearchSchema = z.object({
  /** Clientul din a cărui fișă s-a deschis documentul. */
  client: z.uuid().optional().catch(undefined),
  /** Tabul fișei: din „Servicii" se întoarce la servicii. Implicit, „Documente". */
  tab: z.enum(['documente', 'servicii']).optional().catch(undefined),
});

export type DocumentOrigin = z.infer<typeof documentSearchSchema>;

/** Originea unui document deschis din fișa clientului — ce se pune în `search`. */
export function fromCustomer(
  customerId: string,
  tab: NonNullable<DocumentOrigin['tab']> = 'documente',
): DocumentOrigin {
  return { client: customerId, tab };
}

/** „Înapoi la client" sau „Înapoi la registru", după origine. */
export function DocumentBackLink({ origin }: { origin?: DocumentOrigin }) {
  const customerId = origin?.client;

  return (
    <Button size="small" variant="secondary" asChild>
      {customerId === undefined ? (
        <Link to="/documente">
          <ArrowLeft />
          Înapoi la registru
        </Link>
      ) : (
        <Link
          to="/clienti/$customerId"
          params={{ customerId }}
          search={{ tab: origin?.tab ?? 'documente' }}
        >
          <ArrowLeft />
          Înapoi la client
        </Link>
      )}
    </Button>
  );
}
